/**
 * Available to Invoice by Project Drilldown Component
 * Phase 9.38 - Dashboard Stat Drilldowns
 *
 * Shows unbilled work broken down by project with navigation to project billing
 */
import { useAvailableToInvoiceByProject } from '@/hooks/useDashboardReports';
import { AlertCircle } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDateOnly } from '@/lib/utils';

type SortBy = 'amount' | 'oldest' | 'client';

export function AvailableToInvoiceByProject() {
  const { data, isLoading, error } = useAvailableToInvoiceByProject();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState<SortBy>('amount');

  if (isLoading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4 animate-pulse"></div>
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-100 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        <AlertCircle className="inline mr-2" size={20} />
        Failed to load available to invoice data
      </div>
    );
  }

  const projects = data?.data || [];
  const summary = data?.summary || {
    total_unbilled: 0,
    total_items: 0,
    project_count: 0,
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateStr: string | null) => {
    // oldest_item_date is a DATE column. Parsing it with the bare Date
    // constructor treats it as UTC midnight, which renders one calendar
    // day early west of UTC (e.g. America/Edmonton). formatDateOnly parses
    // the y/m/d components directly so the displayed date always matches
    // the stored calendar date.
    if (!dateStr) return '-';
    return formatDateOnly(dateStr, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Sort projects
  const sortedProjects = [...projects].sort((a, b) => {
    if (sortBy === 'amount') {
      return b.unbilled_amount - a.unbilled_amount;
    } else if (sortBy === 'oldest') {
      const dateA = a.oldest_item_date ? new Date(a.oldest_item_date).getTime() : 0;
      const dateB = b.oldest_item_date ? new Date(b.oldest_item_date).getTime() : 0;
      return dateA - dateB; // Oldest first
    } else {
      return (a.client_name || '').localeCompare(b.client_name || '');
    }
  });

  const handleRowClick = (projectId: string) => {
    navigate(`/projects/${projectId}?section=billing`);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow" data-testid="available-to-invoice-by-project">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          Available to Invoice by Project
        </h2>
        <div className="flex gap-2 text-sm">
          <button
            onClick={() => setSortBy('amount')}
            className={`px-3 py-1 rounded ${sortBy === 'amount' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
          >
            Amount
          </button>
          <button
            onClick={() => setSortBy('oldest')}
            className={`px-3 py-1 rounded ${sortBy === 'oldest' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
          >
            Oldest
          </button>
          <button
            onClick={() => setSortBy('client')}
            className={`px-3 py-1 rounded ${sortBy === 'client' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
          >
            Client
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600">Total Unbilled</div>
          <div className="text-xl font-bold text-sky-600" data-testid="summary-total">
            {formatCurrency(summary.total_unbilled)}
          </div>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600">Billable Items</div>
          <div className="text-xl font-bold text-gray-900" data-testid="summary-items">
            {summary.total_items}
          </div>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600">Projects</div>
          <div className="text-xl font-bold text-gray-900" data-testid="summary-projects">
            {summary.project_count}
          </div>
        </div>
        <div className="bg-orange-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600">Avg per Project</div>
          <div className="text-xl font-bold text-orange-600">
            {summary.project_count > 0
              ? formatCurrency(summary.total_unbilled / summary.project_count)
              : formatCurrency(0)}
          </div>
        </div>
      </div>

      {/* Table */}
      {sortedProjects.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unbilled</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Items</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Oldest Item</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedProjects.map((project) => (
                <tr
                  key={project.project_id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleRowClick(project.project_id)}
                >
                  <td className="px-4 py-3 text-sm">
                    <span className="font-medium text-gray-900">
                      {project.project_number ? `#${project.project_number}` : 'Proposal'}
                    </span>
                    <span className="ml-2 text-gray-600">{project.project_name}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {project.client_name || 'No Client'}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-right text-sky-600">
                    {formatCurrency(project.unbilled_amount)}
                  </td>
                  <td className="px-4 py-3 text-sm text-center text-gray-600">
                    {project.item_count}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {formatDate(project.oldest_item_date)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          No unbilled work found
        </div>
      )}
    </div>
  );
}
